import AddressData from '@/ts/AddressData';
import moment from 'moment';
import { AddressDataResponse, CarbonDateTime } from '@/ts/HococoInterfaces';
import has = Reflect.has;

export default class CalendarEventData {
    public id: number;
    public title: string;
    public body: string | null;
    public address: AddressData = null;
    public eventTime: moment.Moment;
    public startMoment: moment.Moment;
    public endMoment: moment.Moment;
    public isImportant: boolean;
    public createdAt: CarbonDateTime | any;
    public eventStart: string | null = null;
    public eventEnd: string | null = null;
    public creationDate: string;

    public attending: boolean = null;
    public requiresResponse: boolean = false;

    public recipients: number = 0;
    public attenders: number = 0;

    public corporations: any | null;
    public projects: any | null;
    public buildings: any | null;
    public properties: any | null;

    public author: any | null;

    public hasMedia: boolean|null = false;
    public publishedAt: string | null;

    public notificationId: number = null;
    public queueIsExecuted: boolean = false;

    // TODO (CKJ): Convert to moment-based month names
    private monthNames: string[] = [
        'jan', 'feb', 'mar',
        'apr', 'maj', 'jun',
        'jul', 'aug', 'sep',
        'okt', 'nov', 'dec',
    ];

    constructor(
        id: number,
        title: string = '',
        body: string | null = null,
        isImportant = false,
        eventStart: string | null = null,
        eventEnd: string | null = null,
        recipients: number | null = 0,
        attenders: number | null = 0,
        createdAt: CarbonDateTime | any = null,
        corporations: any = null,
        projects: any = null,
        buildings: any = null,
        properties: any = null,
        author: any = null,
        publishedAt: string | null = null,
        notificationId: number = null,
        queueIsExecuted: boolean = false,
    ) {
        this.id = id;
        this.title = title;
        this.body = body;
        this.isImportant = isImportant;
        this.eventStart = eventStart;
        this.eventEnd   = eventEnd;
        this.recipients = recipients;
        this.attenders  = attenders;
        this.createdAt  = createdAt;

        this.corporations = corporations;
        this.projects   = projects;
        this.buildings  = buildings;
        this.properties = properties;
        this.author   = author;
        this.publishedAt   = publishedAt;
        this.notificationId = notificationId;
        this.queueIsExecuted = queueIsExecuted;

        if (createdAt !== null) {
            this.eventTime = typeof createdAt.date === 'undefined' ? moment(eventStart) : moment(eventStart);
        } else {
            this.eventTime = moment();
        }

        this.startMoment = moment(eventStart);
        this.endMoment = moment(eventEnd);
    }

    public day(): string {
        return this.eventTime.format('DD');
    }

    public month(): string {
        return this.eventTime.format('MM');
    }

    public year(): string {
        return this.eventTime.format('DD');
    }

    public toString(): string {
        // L = moment localized
        return this.eventTime.format('L');
    }

    public dayIndex(): string {
        return this.eventTime.format('YYYYMMDD');
    }

    public timeIndex(): string {
        return this.eventTime.unix().toString();
    }

    get projectNames(): string {
        if (!this.projects || this.projects.length === 0) {
            return '';
        }
        return this.projects.map((project: any) => project.name).join(', ');
    }

    get unitGroupNames(): string {
        if (!this.buildings || this.buildings.length === 0) {
            return '';
        }
        return this.buildings.map((building: any) => building.name).join(', ');
    }

    get persisted(): boolean {
        return (this.id !== -1);
    }

    public dataEquals(event: CalendarEventData): boolean {
        // Check if this object's values are equal to the given object's
        if (
            this.id === event.id &&
            this.title === event.title &&
            this.body === event.body &&
            this.address === event.address &&
            this.eventStart === event.eventStart &&
            this.eventEnd === event.eventEnd
        ) {
            return false;
        }
    }

    public addAddressData(address: AddressDataResponse) {
        this.address = new AddressData(
            address.id,
            address.street,
            address.city,
            address.state,
            address.zip,
            address.country_id,
            address.user_id,
        );
    }

    public requestData() {
        return {
            title: this.title,
            description: this.body,
            eventStart: this.eventStart,
            eventEnd: this.eventEnd,
            address: (this.address === null) ? null : this.address.requestData(),
            requiresResponse: this.requiresResponse,
        };
    }

    get sortingCriterion() {
        return this.startMoment.unix();
    }
}
